// store/slices/cart/slice.js
import { createSlice, createSelector, current } from "@reduxjs/toolkit";

const loadCartFromStorage = () => {
  try {
    const serializedCart = localStorage.getItem("cart");
    if (serializedCart === null) {
      return { items: [], totalItems: 0, totalPrice: 0 };
    }
    const cart = JSON.parse(serializedCart);

    // Ako cart nema items array, resetuj ga
    if (!Array.isArray(cart.items)) {
      console.warn("Cart u localStorage nema items array, resetujem...");
      localStorage.removeItem("cart");
      return { items: [], totalItems: 0, totalPrice: 0 };
    }

    // Migracija za postojeće cart item-ove koji nemaju adminUsers podatke
    const needsMigration = cart.items?.some(
      (item) => item.band && !item.band.adminUsers,
    );
    if (needsMigration) {
      localStorage.removeItem("cart");
      return { items: [], totalItems: 0, totalPrice: 0 };
    }

    return cart;
  } catch (err) {
    console.error("Error loading cart from localStorage:", err);
    return { items: [], totalItems: 0, totalPrice: 0 };
  }
};

const saveCartToStorage = (cart) => {
  try {
    const serializedCart = JSON.stringify(cart);
    localStorage.setItem("cart", serializedCart);
  } catch (err) {
    console.error("Error saving cart to localStorage:", err);
  }
};

const calculateTotals = (items) => {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce(
    (sum, item) => sum + (item.salePrice || item.price) * item.quantity,
    0,
  );
  return { totalItems, totalPrice };
};

const getEffectiveStock = (product) => {
  return product.noSize ? (product.stock ?? 0) : (product.stockQuantity ?? 0);
};

const initialState = loadCartFromStorage();

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const { product, quantity = 1, selectedSize = null } = action.payload;

      // Defensive check - ako iz nekog razloga items nije array, inicijalizuj ga
      if (!Array.isArray(state.items)) {
        state.items = [];
      }

      const cartItemId = selectedSize
        ? `${product.id}_${selectedSize}`
        : product.id.toString();

      const existingItem = state.items.find(
        (item) => item.cartItemId === cartItemId,
      );

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        const cartItem = {
          cartItemId,
          id: product.id,
          name: product.name,
          price: product.price,
          salePrice: product.salePrice,
          selectedSize,
          quantity,
          band: product.band,
          category: product.category,
          mainImage: product.mainImage || product.productImages?.[0]?.url,
          stockQuantity: getEffectiveStock(product),
        };
        state.items.push(cartItem);
      }

      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalPrice = totals.totalPrice;

      saveCartToStorage(current(state));
    },

    removeFromCart: (state, action) => {
      if (!Array.isArray(state.items)) state.items = [];
      const cartItemId = action.payload;
      state.items = state.items.filter(
        (item) => item.cartItemId !== cartItemId,
      );

      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalPrice = totals.totalPrice;

      saveCartToStorage(current(state));
    },

    updateQuantity: (state, action) => {
      if (!Array.isArray(state.items)) state.items = [];
      const { cartItemId, quantity } = action.payload;
      const item = state.items.find((item) => item.cartItemId === cartItemId);

      if (item) {
        if (quantity <= 0) {
          state.items = state.items.filter(
            (item) => item.cartItemId !== cartItemId,
          );
        } else {
          item.quantity = quantity;
        }

        const totals = calculateTotals(state.items);
        state.totalItems = totals.totalItems;
        state.totalPrice = totals.totalPrice;

        saveCartToStorage(current(state));
      }
    },

    clearCart: (state) => {
      state.items = [];
      state.totalItems = 0;
      state.totalPrice = 0;
      localStorage.removeItem("cart");
    },

    syncCartWithProducts: (state, action) => {
      if (!Array.isArray(state.items)) state.items = [];
      const products = action.payload;
      state.items = state.items.map((cartItem) => {
        const product = products.find((p) => p.id === cartItem.id);
        if (product) {
          return {
            ...cartItem,
            price: product.price,
            salePrice: product.salePrice,
            stockQuantity: getEffectiveStock(product),
          };
        }
        return cartItem;
      });

      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalPrice = totals.totalPrice;

      saveCartToStorage(current(state));
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
  syncCartWithProducts,
} = cartSlice.actions;

export const selectCart = (state) => state.cart;
export const selectCartItems = (state) => state.cart.items;

export const selectCartTotals = createSelector([selectCart], (cart) => ({
  totalItems: cart.totalItems,
  totalPrice: cart.totalPrice,
}));

export default cartSlice.reducer;
